import React, { useEffect, useState } from 'react';
import Icon from '../shared/Icon';
import { TONE_BADGE } from '../../utils/statusColors';
import { procurementService } from '../../services/api';
import { campo } from '../../design';

/**
 * Registro do DESFECHO da participação (D-III).
 *
 * ## Por que isto não é o `status`
 *
 * `status` diz onde o PROCESSO está — `homologado` significa concluído, por
 * alguém. Quem levou é outra pergunta, e ela já morou dentro do mesmo enum:
 * `ganho`/`perdido` foram depreciados e consolidados em `homologado`, e o
 * placar sumiu junto (121 homologados, 0 ganhos recuperáveis por query).
 *
 * ## ⛔ Captura automática não se digita por cima
 *
 * Quando o robô disputa, o resultado publicado no PNCP é casado pelo CNPJ da
 * IPCOM e o desfecho se preenche sozinho. Isso é documento, não opinião: o
 * componente mostra e trava. O backend recusa igual, então a trava daqui é
 * cortesia com quem clica, não a defesa de verdade.
 */

const OPCOES: { valor: string; rotulo: string; tone: 'success' | 'danger' | 'warning' | 'neutral' }[] = [
  { valor: 'ganho', rotulo: 'Ganhamos', tone: 'success' },
  { valor: 'perdido', rotulo: 'Perdemos', tone: 'danger' },
  { valor: 'desclassificado', rotulo: 'Desclassificados', tone: 'warning' },
  { valor: 'sem_vencedor', rotulo: 'Sem vencedor', tone: 'neutral' },
];

const toneDe = (v?: string | null) => OPCOES.find((o) => o.valor === v)?.tone || 'neutral';
const rotuloDe = (v?: string | null) => OPCOES.find((o) => o.valor === v)?.rotulo || '—';

interface Props {
  itemId: number;
  desfecho?: string | null;
  desfechoFonte?: string | null;
  desfechoObs?: string | null;
  onSalvo?: (item: any) => void;
  showToast?: (msg: string, tipo?: 'success' | 'error') => void;
  /** Compacto: só o selo, sem o formulário (usado onde só se lê). */
  somenteLeitura?: boolean;
  /** Abre o formulário por comando de fora (o CTA "Marcar resultado"). */
  abrirInicial?: boolean;
  onFechar?: () => void;
}

export default function DesfechoPicker({
  itemId, desfecho, desfechoFonte, desfechoObs, onSalvo, showToast, somenteLeitura,
  abrirInicial, onFechar,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const [escolha, setEscolha] = useState(desfecho || '');
  const [obs, setObs] = useState(desfechoObs || '');
  const [salvando, setSalvando] = useState(false);


  const automatico = desfechoFonte === 'automatico';
  const travado = automatico || somenteLeitura;
  // O CTA do cabeçalho abre daqui de fora. Não abre quando o valor veio do
  // PNCP: ali não há o que editar, e piscar um formulário que se recusa a
  // salvar seria pior que não abrir.
  useEffect(() => {
    if (abrirInicial && !travado) setAberto(true);
  }, [abrirInicial, travado]);

  const salvar = async () => {
    if (!escolha) return;
    setSalvando(true);
    try {
      const atualizado = await procurementService.registrarDesfecho(itemId, escolha, obs || undefined);
      showToast?.('Desfecho registrado', 'success');
      setAberto(false);
      onFechar?.();
      onSalvo?.(atualizado);
    } catch (e: any) {
      showToast?.(e?.response?.data?.message || 'Não foi possível registrar o desfecho', 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">Desfecho</div>
      <div className="flex items-center gap-2 mt-0.5">
        {desfecho ? (
          <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${TONE_BADGE[toneDe(desfecho)]}`}>
            {rotuloDe(desfecho)}
          </span>
        ) : (
          <span className="text-xs text-slate-500">não registrado</span>
        )}

        {automatico && (
          <span
            className="text-[10px] text-slate-500 flex items-center gap-1"
            title="Capturado do resultado publicado no PNCP, casado pelo CNPJ da IPCOM. Não é editável à mão."
          >
            <Icon name="check" className="w-3 h-3" />
            PNCP
          </span>
        )}

        {!travado && (
          <button
            onClick={() => setAberto((v) => !v)}
            className="text-[11px] text-orange-400 hover:text-orange-300 transition-colors"
          >
            {desfecho ? 'alterar' : 'registrar'}
          </button>
        )}
      </div>

      {desfechoObs && !aberto && (
        <div className="text-[11px] text-slate-500 mt-1 italic">{desfechoObs}</div>
      )}

      {aberto && !travado && (
        <div className="mt-2 space-y-2 bg-slate-900/60 border border-slate-700 rounded-lg p-3">
          <div className="flex flex-wrap gap-1.5">
            {OPCOES.map((o) => (
              <button
                key={o.valor}
                onClick={() => setEscolha(o.valor)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  escolha === o.valor
                    ? `${TONE_BADGE[o.tone]} border-transparent`
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                {o.rotulo}
              </button>
            ))}
          </div>
          <input
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            maxLength={500}
            placeholder="Observação (ex.: perdemos na habilitação)"
            className={`w-full rounded-lg px-3 py-1.5 text-xs outline-none ${campo}`}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={salvar}
              disabled={!escolha || salvando}
              className="px-3 py-1.5 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-[11px] font-medium transition-colors"
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              onClick={() => { setAberto(false); onFechar?.(); }}
              className="px-3 py-1.5 text-slate-400 hover:text-slate-200 text-[11px] transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
